import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { store } from './store/Store';
import Spinner from './views/spinner/Spinner';
import ErrorBoundaryFallback from './components/ErrorBoundaryFallback';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import './_mockApis';
import './utils/i18n';
import './index.css';
import * as Sentry from '@sentry/react';
import { applySentryRuntimeContext, getSentryRuntimeContext } from './utils/sentryContext';

Sentry.init({
  dsn: 'https://db67df0b3d6a9d70d3d60a6f51087a38@o4507306638508032.ingest.us.sentry.io/4507306640080896',
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  tracesSampleRate: 0.1,
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 1.0,
  attachStacktrace: true,
  normalizeDepth: 8,
  beforeSend(event, hint) {
    const ctx = getSentryRuntimeContext();
    event.tags = event.tags || {};
    if (ctx.bizId) {
      event.tags.bizId = ctx.bizId;
    }
    if (ctx.language) {
      event.tags.language = ctx.language;
    }
    if (ctx.route) {
      event.tags.route = ctx.route;
    }
    event.contexts = event.contexts || {};
    event.contexts.app_runtime = ctx;

    const original = hint?.originalException;
    if (original && typeof original === 'object') {
      event.extra = event.extra || {};
      if (original.name && !event.extra.errorName) {
        event.extra.errorName = original.name;
      }
      if (original.message && !event.extra.errorMessage) {
        event.extra.errorMessage = original.message;
      }
      if (original.stack && !event.extra.errorStack) {
        event.extra.errorStack = original.stack;
      }
    }

    return event;
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'), {
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
  onRecoverableError: Sentry.reactErrorHandler(),
});

root.render(
  <ChunkErrorBoundary>
    <Sentry.ErrorBoundary
      beforeCapture={(scope, error, componentStack) => {
        applySentryRuntimeContext(scope);
        scope.setLevel('fatal');
        scope.setTag('error_boundary', 'true');
        scope.setTag('fatal_ui', 'true');
        if (componentStack) {
          scope.setExtra('componentStack', componentStack);
        }
        if (error) {
          scope.setExtra('errorName', error.name);
          scope.setExtra('errorMessage', error.message);
          scope.setExtra('errorStack', error.stack);
        }
      }}
      fallback={({ error, eventId, resetError }) => (
        <ErrorBoundaryFallback error={error} eventId={eventId} resetError={resetError} />
      )}
    >
      <Provider store={store}>
        <Suspense fallback={<Spinner />}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </Suspense>
      </Provider>
    </Sentry.ErrorBoundary>
  </ChunkErrorBoundary>,
);
